/**
 * Local Journal Storage (IndexedDB)
 * For personal use without Supabase
 */

import Dexie, { type Table } from 'dexie';
import type { JournalEntry } from '@/lib/services/journal';

class LocalJournalDB extends Dexie {
  entries!: Table<JournalEntry, string>;

  constructor() {
    super('LocalJournalDB');
    this.version(1).stores({
      entries: 'id, user_id, created_at',
    });
  }
}

const journalDb = new LocalJournalDB();
const GUEST_USER_ID = 'guest-user';

export async function createLocalJournalEntry(input: {
  content: string;
  tags?: string[];
}): Promise<JournalEntry> {
  const entry: JournalEntry = {
    id: `entry-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    user_id: GUEST_USER_ID,
    encrypted_content: input.content, // Store as plaintext for local use
    iv: '',
    tags: input.tags || [],
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    is_encrypted: false,
  };

  await journalDb.entries.add(entry);
  return entry;
}

export async function getLocalJournalEntries(limit = 50): Promise<JournalEntry[]> {
  return await journalDb.entries
    .where('user_id')
    .equals(GUEST_USER_ID)
    .orderBy('created_at')
    .reverse()
    .limit(limit)
    .toArray();
}

export async function getLocalJournalEntry(entryId: string): Promise<{
  id: string;
  content: string;
  tags: string[];
  created_at: string;
  updated_at: string;
  is_encrypted?: boolean;
} | null> {
  const entry = await journalDb.entries.get(entryId);
  if (!entry) return null;

  return {
    id: entry.id,
    content: entry.encrypted_content, // Plaintext for local
    tags: entry.tags,
    created_at: entry.created_at,
    updated_at: entry.updated_at,
    is_encrypted: false,
  };
}

export async function updateLocalJournalEntry(
  entryId: string,
  input: { content: string; tags?: string[] }
): Promise<JournalEntry> {
  const entry = await journalDb.entries.get(entryId);
  if (!entry) throw new Error('Entry not found');

  const updated: JournalEntry = {
    ...entry,
    encrypted_content: input.content,
    tags: input.tags || entry.tags,
    updated_at: new Date().toISOString(),
  };

  await journalDb.entries.update(entryId, updated);
  return updated;
}

export async function deleteLocalJournalEntry(entryId: string): Promise<void> {
  await journalDb.entries.delete(entryId);
}



